import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  Calendar,
  Download,
  Eye,
  FileSpreadsheet,
  Heart,
  Loader2,
  Package,
  Phone,
  QrCode,
  Users,
} from "lucide-react";
import type { ExhibitorAnalyticsDto, LeadStatus } from "@mvk/shared";
import { useAdminAuth } from "../../auth/AdminAuthContext";
import { adminAnalyticsApi } from "../../../services/adminAnalyticsApi";
import { ApiError } from "../../../api/client";
import { downloadTableCsv, downloadTableExcel } from "../../../utils/exportTable";
import { toast } from "sonner";

const LEAD_STATUS_LABEL: Record<string, string> = {
  new: "Новый",
  contacted: "Связались",
  in_progress: "В работе",
  qualified: "Квалиф.",
  closed: "Закрыт",
  rejected: "Отказ",
};

export function ExhibitorAnalyticsPage() {
  const { user } = useAdminAuth();
  const navigate = useNavigate();
  const eventId = user?.assignedEventId ?? "";
  const companyId = user?.assignedCompanyId ?? "";
  const [data, setData] = useState<ExhibitorAnalyticsDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [disabled, setDisabled] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!eventId || !companyId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      setDisabled(false);
      try {
        const res = await adminAnalyticsApi.getExhibitor(eventId, companyId);
        if (!cancelled) setData(res.analytics);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.code === "analytics_disabled") {
          setDisabled(true);
          setData(null);
        } else {
          setError(err instanceof Error ? err.message : "Не удалось загрузить");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [eventId, companyId]);

  if (loading) {
    return (
      <div className="p-6 flex justify-center text-gray-400">
        <Loader2 size={22} className="animate-spin" />
      </div>
    );
  }

  const exportScanners = (format: "csv" | "excel") => {
    if (!data?.scanners.length) {
      toast.error("Нет данных для выгрузки");
      return;
    }
    const headers = [
      "Посетитель",
      "Город",
      "Телефон",
      "Email",
      "Сканы",
      "Статус лида",
      "В приложении",
      "Последний скан",
    ];
    const rows = data.scanners.map((s) => [
      s.fullName,
      s.city ?? "",
      s.phone ?? "",
      s.email ?? "",
      s.scanCount,
      s.leadStatus ? LEAD_STATUS_LABEL[s.leadStatus] ?? s.leadStatus : "",
      s.lastSeenAt ? new Date(s.lastSeenAt).toLocaleString("ru-RU") : "",
      new Date(s.lastScannedAt).toLocaleString("ru-RU"),
    ]);
    const base = `scanners-${eventId}-${companyId}`;
    try {
      if (format === "csv") {
        downloadTableCsv(base, headers, rows);
      } else {
        downloadTableExcel(base, headers, rows, "Вас сканировали");
      }
      toast.success(format === "csv" ? "CSV загружен" : "Excel загружен");
    } catch {
      toast.error("Не удалось выгрузить таблицу");
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-5">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate("/exhibitor")}
          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"
          aria-label="Назад"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="min-w-0 flex-1">
          <h1 className="text-[19px] text-gray-900">Аналитика интереса</h1>
          <p className="text-[13px] text-gray-500 truncate">{data?.companyName ?? "Ваш стенд"}</p>
        </div>
        {data && (
          <button
            type="button"
            onClick={() => navigate("/exhibitor/leads")}
            className="hidden sm:inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-indigo-50 text-indigo-600 text-[12px] hover:bg-indigo-100"
          >
            Все лиды <ArrowRight size={12} />
          </button>
        )}
      </div>

      {disabled && (
        <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 text-[13px] text-amber-800">
          Организатор пока не открыл вам доступ к этой аналитике. Обратитесь к куратору выставки.
        </div>
      )}

      {error && <p className="text-[13px] text-red-600">{error}</p>}

      {data && (
        <>
          {/* KPI strip */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <div className="bg-white rounded-xl border border-gray-100 p-3 sm:p-4">
              <Eye size={16} className="text-indigo-500 mb-1.5" />
              <div className="text-[18px] sm:text-[20px] text-gray-900">{data.companyViews}</div>
              <div className="text-[11px] sm:text-[12px] text-gray-500">Просмотры профиля</div>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-3 sm:p-4">
              <Package size={16} className="text-violet-500 mb-1.5" />
              <div className="text-[18px] sm:text-[20px] text-gray-900">{data.productViews}</div>
              <div className="text-[11px] sm:text-[12px] text-gray-500">Просмотры товаров</div>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-3 sm:p-4">
              <QrCode size={16} className="text-emerald-500 mb-1.5" />
              <div className="text-[18px] sm:text-[20px] text-gray-900">{data.contactScans}</div>
              <div className="text-[11px] sm:text-[12px] text-gray-500">Всего сканов</div>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-3 sm:p-4">
              <Users size={16} className="text-sky-500 mb-1.5" />
              <div className="text-[18px] sm:text-[20px] text-gray-900">{data.uniqueVisitors}</div>
              <div
                className="text-[11px] sm:text-[12px] text-gray-500"
                title="Сколько уникальных людей взаимодействовали со стендом (просмотр профиля, товаров и др.), без повторного учёта одного и того же посетителя"
              >
                Уник. интерес
              </div>
              <p className="text-[10px] text-gray-400 mt-1 leading-snug">
                Уникальные посетители стенда, не сумма просмотров
              </p>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-3 sm:p-4">
              <Heart size={16} className="text-rose-500 mb-1.5" />
              <div className="text-[18px] sm:text-[20px] text-gray-900">{data.productFavorites ?? 0}</div>
              <div className="text-[11px] sm:text-[12px] text-gray-500">В избранное (товары)</div>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-3 sm:p-4">
              <Calendar size={16} className="text-amber-500 mb-1.5" />
              <div className="text-[18px] sm:text-[20px] text-gray-900">{data.sessionFavorites ?? 0}</div>
              <div className="text-[11px] sm:text-[12px] text-gray-500">Избранные сессии</div>
            </div>
          </div>

          {/* Affinity */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <section className="bg-white rounded-xl border border-gray-100 p-4">
              <div className="flex items-center gap-2 mb-3">
                <Building2 size={14} className="text-indigo-500" />
                <h2 className="text-[13px] text-gray-900">Кого ещё смотрели ваши лиды</h2>
              </div>
              <p className="text-[11px] text-gray-400 mb-3 leading-snug">
                Анализ посетителей стенда: визиты представителей профильных компаний свидетельствуют
                о целевом интересе; массовое сканирование без разбора указывает на случайный обход
                выставки.
              </p>
              {data.relatedExhibitors.length === 0 ? (
                <p className="text-[12px] text-gray-400">Пока нет пересечений</p>
              ) : (
                <div className="space-y-2">
                  {data.relatedExhibitors.map((row, i) => (
                    <div key={row.companyId} className="flex items-center gap-2 text-[12px]">
                      <span className="text-gray-400 w-4">{i + 1}</span>
                      <span className="flex-1 truncate text-gray-800">{row.name}</span>
                      <span className="text-indigo-600 flex-shrink-0">{row.visitorCount}</span>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="bg-white rounded-xl border border-gray-100 p-4">
              <div className="flex items-center gap-2 mb-3">
                <Package size={14} className="text-violet-500" />
                <h2 className="text-[13px] text-gray-900">Какие товары ещё смотрели</h2>
              </div>
              <p className="text-[11px] text-gray-400 mb-3 leading-snug">
                Товары конкурентов, отсканированные вашими посетителями, помогают определить сферу
                их интересов.
              </p>
              {data.relatedProducts.length === 0 ? (
                <p className="text-[12px] text-gray-400">Пока нет пересечений</p>
              ) : (
                <div className="space-y-2">
                  {data.relatedProducts.map((row, i) => (
                    <div key={row.productId} className="flex items-center gap-2 text-[12px]">
                      <span className="text-gray-400 w-4">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <div className="truncate text-gray-800">{row.name}</div>
                        {row.companyName ? (
                          <div className="truncate text-[10px] text-gray-400">{row.companyName}</div>
                        ) : null}
                      </div>
                      <span className="text-indigo-600 flex-shrink-0">{row.visitorCount}</span>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>

          {/* Managers / profiling moved to «Вы сканировали» */}

          {/* Scanners table */}
          <section className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="flex items-center justify-between gap-2 px-4 pt-4 pb-2">
              <div className="min-w-0">
                <h2 className="text-[13px] text-gray-900">Вас сканировали</h2>
                <p className="text-[11px] text-gray-400">
                  Контакты посетителей · до 50 последних · выгрузка Excel/CSV
                </p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => exportScanners("csv")}
                  disabled={data.scanners.length === 0}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] text-indigo-600 hover:bg-indigo-50 disabled:opacity-40"
                >
                  <Download size={12} /> CSV
                </button>
                <button
                  type="button"
                  onClick={() => exportScanners("excel")}
                  disabled={data.scanners.length === 0}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] text-indigo-600 hover:bg-indigo-50 disabled:opacity-40"
                >
                  <FileSpreadsheet size={12} /> Excel
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/exhibitor/leads")}
                  className="sm:hidden inline-flex items-center gap-1 text-[12px] text-indigo-600"
                >
                  Лиды <ArrowRight size={12} />
                </button>
              </div>
            </div>

            {data.scanners.length === 0 ? (
              <p className="px-4 pb-4 text-[12px] text-gray-400">Пока нет сканов</p>
            ) : (
              <>
                {/* Desktop table */}
                <div className="hidden sm:block overflow-x-auto">
                  <table className="w-full text-left text-[12px]">
                    <thead>
                      <tr className="border-t border-gray-50 text-gray-400">
                        <th className="px-4 py-2 font-normal">Посетитель</th>
                        <th className="px-2 py-2 font-normal">Город</th>
                        <th className="px-2 py-2 font-normal">Телефон</th>
                        <th className="px-2 py-2 font-normal">Email</th>
                        <th className="px-2 py-2 font-normal text-right">Сканы</th>
                        <th className="px-2 py-2 font-normal">Лид</th>
                        <th className="px-2 py-2 font-normal text-right">В приложении</th>
                        <th className="px-4 py-2 font-normal text-right">Скан</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.scanners.map((s) => (
                        <tr
                          key={s.userId}
                          className={`border-t border-gray-50 ${s.leadId ? "hover:bg-gray-50 cursor-pointer" : ""}`}
                          onClick={() => {
                            if (s.leadId) navigate(`/exhibitor/leads/${s.leadId}`);
                          }}
                        >
                          <td className="px-4 py-2.5 text-gray-900 max-w-[140px] truncate">{s.fullName}</td>
                          <td className="px-2 py-2.5 text-gray-600 whitespace-nowrap max-w-[100px] truncate">
                            {s.city || "—"}
                          </td>
                          <td className="px-2 py-2.5 text-gray-600 whitespace-nowrap">{s.phone || "—"}</td>
                          <td className="px-2 py-2.5 text-gray-600 max-w-[160px] truncate">{s.email || "—"}</td>
                          <td className="px-2 py-2.5 text-indigo-600 text-right">{s.scanCount}</td>
                          <td className="px-2 py-2.5 text-gray-500">
                            {s.leadStatus
                              ? LEAD_STATUS_LABEL[s.leadStatus as LeadStatus] ?? s.leadStatus
                              : "—"}
                          </td>
                          <td className="px-2 py-2.5 text-gray-400 text-right whitespace-nowrap">
                            {s.lastSeenAt
                              ? new Date(s.lastSeenAt).toLocaleString("ru-RU", {
                                  day: "2-digit",
                                  month: "2-digit",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })
                              : "—"}
                          </td>
                          <td className="px-4 py-2.5 text-gray-400 text-right whitespace-nowrap">
                            {new Date(s.lastScannedAt).toLocaleString("ru-RU", {
                              day: "2-digit",
                              month: "2-digit",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile cards */}
                <div className="sm:hidden divide-y divide-gray-50 border-t border-gray-50">
                  {data.scanners.map((s) => (
                    <button
                      key={s.userId}
                      type="button"
                      disabled={!s.leadId}
                      onClick={() => {
                        if (s.leadId) navigate(`/exhibitor/leads/${s.leadId}`);
                      }}
                      className="w-full text-left px-4 py-3 space-y-1 disabled:opacity-90"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[13px] text-gray-900 truncate">{s.fullName}</span>
                        <span className="text-[12px] text-indigo-600 flex-shrink-0">{s.scanCount}×</span>
                      </div>
                      {(s.city || s.phone) && (
                        <div className="flex items-center gap-1 text-[11px] text-gray-500">
                          {s.city ? <span className="truncate">{s.city}</span> : null}
                          {s.city && s.phone ? <span>·</span> : null}
                          {s.phone ? (
                            <>
                              <Phone size={11} />
                              <span>{s.phone}</span>
                            </>
                          ) : null}
                        </div>
                      )}
                      {!s.city && !s.phone ? (
                        <div className="flex items-center gap-1 text-[11px] text-gray-500">
                          <Phone size={11} />
                          <span>нет телефона</span>
                        </div>
                      ) : null}
                      {s.email ? (
                        <div className="text-[11px] text-gray-400 truncate">{s.email}</div>
                      ) : null}
                      <div className="flex items-center justify-between text-[10px] text-gray-400">
                        <span>
                          {s.leadStatus
                            ? LEAD_STATUS_LABEL[s.leadStatus as LeadStatus] ?? s.leadStatus
                            : "Без лида"}
                        </span>
                        <span>
                          {new Date(s.lastScannedAt).toLocaleString("ru-RU", {
                            day: "2-digit",
                            month: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </>
            )}
          </section>

          {/* Favorites */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <section className="bg-white rounded-xl border border-gray-100 p-4">
              <div className="flex items-center gap-2 mb-3">
                <Heart size={14} className="text-rose-500" />
                <h2 className="text-[13px] text-gray-900">Ваши товары в избранном</h2>
              </div>
              {(data.favoritedProducts?.length ?? 0) === 0 ? (
                <p className="text-[12px] text-gray-400">Пока никто не добавлял</p>
              ) : (
                <div className="space-y-2">
                  {data.favoritedProducts!.map((row, i) => (
                    <div key={row.productId} className="flex items-center gap-2 text-[12px]">
                      <span className="text-gray-400 w-4">{i + 1}</span>
                      <span className="flex-1 truncate text-gray-800">{row.name}</span>
                      <span className="text-rose-600 flex-shrink-0">{row.favorites}</span>
                    </div>
                  ))}
                </div>
              )}
            </section>
            <section className="bg-white rounded-xl border border-gray-100 p-4">
              <div className="flex items-center gap-2 mb-3">
                <Calendar size={14} className="text-amber-500" />
                <h2 className="text-[13px] text-gray-900">Сессии программы у ваших гостей</h2>
              </div>
              <p className="text-[11px] text-gray-400 mb-3">
                Что из программы добавили в избранное люди, интересовавшиеся вами
              </p>
              {(data.favoritedSessions?.length ?? 0) === 0 ? (
                <p className="text-[12px] text-gray-400">Пока нет данных</p>
              ) : (
                <div className="space-y-2">
                  {data.favoritedSessions!.map((row, i) => (
                    <div key={row.sessionId} className="flex items-center gap-2 text-[12px]">
                      <span className="text-gray-400 w-4">{i + 1}</span>
                      <span className="flex-1 truncate text-gray-800">{row.title}</span>
                      <span className="text-amber-600 flex-shrink-0">{row.favorites}</span>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>

          {/* Own top products */}
          <section className="bg-white rounded-xl border border-gray-100 p-4">
            <h2 className="text-[13px] text-gray-900 mb-1">Топ ваших товаров</h2>
            <p className="text-[11px] text-gray-400 mb-3">
              Сколько раз сканировали и кто из посетителей
            </p>
            {data.topProducts.length === 0 ? (
              <p className="text-[12px] text-gray-400">Пока нет данных</p>
            ) : (
              <div className="space-y-3">
                {data.topProducts.map((p, i) => (
                  <div key={p.id} className="border-b border-gray-50 last:border-0 pb-3 last:pb-0">
                    <div className="flex items-center gap-3 text-[12px]">
                      <span className="text-gray-400 w-4">{i + 1}</span>
                      <span className="flex-1 truncate text-gray-800">{p.name}</span>
                      <span className="text-gray-500">{p.views} просм.</span>
                      <span className="text-indigo-600">{p.scans} скан.</span>
                    </div>
                    {(p.scanners?.length ?? 0) > 0 ? (
                      <div className="mt-1.5 ml-7 space-y-1">
                        {p.scanners!.map((s) => (
                          <button
                            key={`${p.id}-${s.userId}`}
                            type="button"
                            className="flex w-full items-center gap-2 text-[11px] text-left hover:bg-gray-50 rounded-lg px-1.5 py-1"
                            onClick={() => {
                              if (s.leadId) navigate(`/exhibitor/leads/${s.leadId}`);
                            }}
                          >
                            <span className="flex-1 truncate text-gray-700">{s.fullName}</span>
                            {s.phone ? (
                              <span className="text-gray-400 flex-shrink-0">{s.phone}</span>
                            ) : null}
                            <span className="text-indigo-600 flex-shrink-0">{s.scanCount}×</span>
                          </button>
                        ))}
                      </div>
                    ) : p.scans > 0 ? (
                      <p className="mt-1 ml-7 text-[11px] text-gray-400">Нет детализации по посетителям</p>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
